import type { CallScript } from "@frontierx/shared/schemas/CallScript";
import type { ScriptDetectionLogPayload } from "@frontierx/shared/schemas/clientMessageTypes";
import type { Call, LogMessage } from "@frontierx/types";
import { Loader2, Phone, RefreshCw, Settings } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useSessionStorage } from "usehooks-ts";
import { useToast } from "@/components/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ToastAction } from "@/components/ui/toast";
import type { CompatibleSocket } from "~/app/types/CompatibleSocket";
import { useAnalytics } from "../Analytics/AnalyticsProvider";
import { useUser } from "../Auth/UnifiedAuthProvider";
import { StartCallModal } from "../Dashboard/StartCallModal";
import { SettingsManagerUI } from "../Settings/SettingsManagerUI";
import { AppHeader } from "../UI/AppHeader";
import { CallDisplay } from "./CallDisplay";
import { NowNextRecentCalls } from "./NowNextRecentCalls";

declare const __APP_VERSION__: string;

export interface CallManagerUIProps {
  calls: Call[];
  selectedCall: string | null;
  error: string | null;
  logs?: LogMessage[];
  scriptDetectionLogs?: ScriptDetectionLogPayload[];
  isLoading?: boolean;
  createCall: (url: string, callScript: CallScript) => void;
  deleteCall: (roomId: string) => void;
  selectCall: (roomId: string) => void;
  leaveCall: () => void;
  refreshCalls?: () => void;
  isFetching?: boolean;
  mainSocket?: CompatibleSocket | null;
  clearLogs?: () => void;
  clearScriptDetectionLogs?: () => void;
  swapCallScript?: (callId: string, callScript: CallScript) => Promise<void>;
}

export function CallManagerUI({
  calls,
  selectedCall,
  error,
  logs: _logs = [],
  scriptDetectionLogs: _scriptDetectionLogs = [],
  isLoading = false,
  isFetching = false,
  createCall,
  deleteCall: _deleteCall,
  selectCall,
  leaveCall,
  refreshCalls,
  mainSocket,
  clearLogs: _clearLogs,
  clearScriptDetectionLogs: _clearScriptDetectionLogs,
  swapCallScript: _swapCallScript,
}: Readonly<CallManagerUIProps>) {
  const { user: _user } = useUser();
  const { trackEvent } = useAnalytics();
  const [showSettings, setShowSettings] = useState(false);
  const [isCreatingCall, setIsCreatingCall] = useState(false);
  const [currentCallSession, setCurrentCallSession] = useSessionStorage(
    "currentCallSession",
    "",
  );
  const { toast } = useToast();
  const lastErrorRef = useRef<string | null>(null);

  useEffect(() => {
    if (error && error !== lastErrorRef.current) {
      lastErrorRef.current = error;

      const mainSocketStatus = mainSocket?.readyState;
      let socketInfo = "";
      if (mainSocketStatus !== 1) {
        socketInfo += "Main connection disconnected. ";
      }

      let userFriendlyError = error;
      if (error.includes("Failed to fetch")) {
        userFriendlyError =
          "Unable to connect to the server. Please check your internet connection.";
      } else if (error.includes("WebSocket connection error")) {
        userFriendlyError =
          "Lost connection to the server. Some features may be unavailable.";
      }

      toast({
        variant: "destructive",
        title: "Error",
        description: `${socketInfo}${userFriendlyError}`,
        duration: 10000,
        action: (
          <ToastAction
            altText="Dismiss error"
            onClick={() => {
              lastErrorRef.current = null;
            }}
          >
            Dismiss
          </ToastAction>
        ),
      });
    }
  }, [error, toast, mainSocket]);

  useEffect(() => {
    if (!error) {
      lastErrorRef.current = null;
    }
  }, [error]);

  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };

  const leaveSettings = () => {
    setShowSettings(false);
  };

  useEffect(() => {
    if (selectedCall && selectedCall !== currentCallSession) {
      trackEvent("user_joined_call", {
        roomId: selectedCall,
        previousRoomId: currentCallSession,
      });
      setCurrentCallSession(selectedCall);
    }
  }, [selectedCall, currentCallSession, setCurrentCallSession, trackEvent]);

  const handleCreateCall = async (url: string, callScript: CallScript) => {
    setIsCreatingCall(true);

    try {
      createCall(url.trim(), callScript);
      console.log("Call creation initiated");

      trackEvent("call_created", {
        url: url.trim(),
        scriptId: callScript.id,
      });
    } catch (error) {
      console.error("Error creating call:", error);
      setIsCreatingCall(false);
      return;
    }

    setTimeout(() => {
      setIsCreatingCall(false);
    }, 5000);
  };

  const handleRefresh = () => {
    if (refreshCalls) {
      refreshCalls();
    }
  };

  return (
    <>
      {selectedCall === null && !showSettings && (
        <div className="flex h-full flex-col select-none">
          <div className="flex flex-grow flex-col space-y-2 min-h-0">
            {/* Quick Actions Header */}
            <AppHeader title="Your calls">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                className="flex items-center gap-1 text-xs h-6 px-2 text-gray-600 hover:text-gray-900"
                title="Refresh calendar events and calls"
              >
                <RefreshCw className="h-3 w-3" />
              </Button>
              <Button
                onClick={toggleSettings}
                variant="ghost"
                size="sm"
                data-test="settings-button"
                title="Settings"
                className="flex items-center gap-1 text-xs h-6 px-2 text-gray-600 hover:text-gray-900"
              >
                <Settings className="h-3 w-3" />
              </Button>
              {mainSocket && (
                <StartCallModal
                  onCreateCall={handleCreateCall}
                  mainSocket={mainSocket}
                  isCreating={isCreatingCall}
                >
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1 text-xs h-6 px-2 text-gray-600 hover:text-gray-900"
                  >
                    <Phone className="h-3 w-3" />
                    Join call
                  </Button>
                </StartCallModal>
              )}
            </AppHeader>

            {/* Main Calls Display */}
            <div className="flex-grow min-h-0">
              {isLoading && calls.length === 0 ? (
                <div className="flex h-full items-center justify-center select-none">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-500">Loading calls...</span>
                </div>
              ) : (
                <div className="relative h-full">
                  {isFetching && (
                    <div className="absolute top-2 left-2 z-10 flex items-center gap-2 text-xs text-gray-500 bg-white/80 backdrop-blur-sm rounded-md px-2 py-1">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span>Updating...</span>
                    </div>
                  )}
                  <ScrollArea className="h-full">
                    <div>
                      <NowNextRecentCalls
                        calls={calls}
                        onJoinCall={selectCall}
                        onViewReport={(callId) => {
                          // Navigate to dash app for call report
                          const dashUrl = import.meta.env.VITE_DASH_URL;
                          if (dashUrl) {
                            window.open(`${dashUrl}/calls/${callId}`, "_blank");
                          }
                        }}
                      />
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          </div>

          {/* Version footer - always visible */}
          <div className="flex-0 flex-shrink-0 p-4">
            <p className="text-xs uppercase text-gray-400 select-none">
              Version: {__APP_VERSION__}
            </p>
          </div>
        </div>
      )}

      {selectedCall && !showSettings && mainSocket && (
        <CallDisplay
          mainSocket={mainSocket}
          roomId={selectedCall}
          leaveCall={leaveCall}
        />
      )}

      {showSettings && mainSocket && (
        <SettingsManagerUI partySocket={mainSocket} onClose={leaveSettings} />
      )}
    </>
  );
}
